import type { FC, ReactNode } from 'react';
import { Map, Globe, CheckCircle2, PenLine, Calendar } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useMemberCountriesStats } from '@/lib/api/hooks/memberCountry';
import { Loader } from '@/components/ui';
import formatDate from '@/utils/helpers/formatDate';

interface StatsCardProps {
  title: string;
  value: number | string;
  description: string;
  icon: ReactNode;
  iconContainerClassName: string;
  iconClassName: string;
}

type StatKey = 'totalRegions' | 'totalCountries' | 'publishedCountries' | 'draftCountries' | "latestUpdatedAt";

interface StatConfig {
  key: StatKey;
  title: string;
  icon: LucideIcon;
  iconContainerClassName: string;
  iconClassName: string;
  getDescription: (values: Record<StatKey, number | string>) => string;
}

const StatCard = ({
  title,
  value,
  description,
  icon,
  iconContainerClassName,
  iconClassName,
}: StatsCardProps) => (
  <article className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
    <div className="grid grid-cols-[auto_1fr] gap-4">
      <div className={`flex h-12 w-12 items-center justify-center rounded ${iconContainerClassName}`}>
        <span className={iconClassName}>{icon}</span>
      </div>
      <div>
        <p className="text-base mb-1 font-medium text-gray-600">{title}</p>
        <p className={`leading-[1.1] font-semibold text-gray-900 ${title.toLowerCase() === 'last updated' ? 'text-[1.25rem]' : 'text-[2rem]'}`}>{value}</p>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </div>
  </article>
);

const STATS_CONFIG: StatConfig[] = [
  {
    key: 'totalRegions',
    title: 'Total Regions',
    icon: Map,
    iconContainerClassName: 'bg-[#F8E8EB70]',
    iconClassName: 'text-[#8F2F44]',
    getDescription: () => 'Active regional groupings',
  },
  {
    key: 'totalCountries',
    title: 'Total Countries',
    icon: Globe,
    iconContainerClassName: 'bg-[#E8F6EE70]',
    iconClassName: 'text-[#0F8A47]',
    getDescription: () => 'Across all regions',
  },
  {
    key: 'publishedCountries',
    title: 'Published',
    icon: CheckCircle2,
    iconContainerClassName: 'bg-[#E8F0FE70]',
    iconClassName: 'text-[#1A56DB]',
    getDescription: () => 'Countries published',
  },
  {
    key: 'draftCountries',
    title: 'Drafts',
    icon: PenLine,
    iconContainerClassName: 'bg-[#FDF3E670]',
    iconClassName: 'text-[#B86A00]',
    getDescription: () => 'Countries in draft',
  },
  {
    key: 'latestUpdatedAt',
    title: 'Last Updated',
    icon: Calendar,
    iconContainerClassName: 'bg-[#9333ea10]',
    iconClassName: 'text-[#9333ea]',
    getDescription: () => '',
  }
];

const MemberCountriesStats: FC = () => {
  const { data: stats, isFetching, refetch, isError } = useMemberCountriesStats();

  const values: Record<StatKey, number|string> = {
    totalRegions: stats?.totalRegions ?? 0,
    totalCountries: stats?.totalCountries ?? 0,
    publishedCountries: stats?.publishedCountries ?? 0,
    draftCountries: stats?.draftCountries ?? 0,
    latestUpdatedAt: stats?.latestUpdatedAt ? formatDate(stats.latestUpdatedAt, 'DD MMM YYYY') : "",
  };

  return (
    <section className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
      {isError ? (
        <div className="col-span-full p-4 flex flex-col items-center justify-center gap-4">
          <p>Error loading statistics.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-forestGreen-100 text-white rounded">
            Retry
          </button>
        </div>
      ) : isFetching ? (
        STATS_CONFIG.map((stat) => (
          <div key={stat.key} className="p-4 flex items-center justify-center min-h-28 w-full">
            <Loader size="md" variant="wine" />
          </div>
        ))
      ) : (
        STATS_CONFIG.map((stat) => {
          const Icon = stat.icon;
          return (
            <StatCard
              key={stat.key}
              title={stat.title}
              value={values[stat.key]}
              description={stat.getDescription(values)}
              icon={<Icon className="h-4 w-4 md:h-6 md:w-6"  />}
              iconContainerClassName={stat.iconContainerClassName}
              iconClassName={stat.iconClassName}
            />
          );
        })
      )}
    </section>
  );
};

export default MemberCountriesStats;
